import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Eye, Phone, MessageCircle, Download } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, API, getToken, formatErr } from "../api";
import { useCatalog } from "../../contexts/CatalogContext";

export default function AdminCustomers() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { categories: CATEGORIES } = useCatalog();

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("q", search);
      qs.set("limit", String(pageSize));
      qs.set("skip", String((page - 1) * pageSize));
      const { data: d } = await adminAxios.get(`/customers?${qs.toString()}`);
      setData({ total: d.total || 0, items: d.items || [] });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, pageSize, search]);

  const exportCsv = () => {
    const qs = new URLSearchParams();
    if (search) qs.set("q", search);
    const url = qs.toString() ? `${API}/admin/customers/export.csv?${qs.toString()}` : `${API}/admin/customers/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "repairro-customers.csv";
        a.click();
      });
  };

  return (
    <AdminLayout title="Customers">
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] items-end">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearch(e.target.value)}
              data-testid="customer-search"
              placeholder="Search by name or phone"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-[#1A3C6E] outline-none"
            />
          </div>
          <button
            onClick={exportCsv}
            data-testid="export-customers-csv"
            className="inline-flex items-center gap-2 justify-center bg-[#1A3C6E] hover:bg-[#132D53] text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm text-slate-500">
          {loading ? "Loading…" : `Showing ${Math.min((page - 1) * pageSize + 1, data.total)} - ${Math.min(page * pageSize, data.total)} of ${data.total} customers`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Phone</th>
                <th className="px-5 py-2.5">City</th>
                <th className="px-5 py-2.5">Segment</th>
                <th className="px-5 py-2.5">Services Used</th>
                <th className="px-5 py-2.5">Total Bookings</th>
                <th className="px-5 py-2.5">Last Booking</th>
                <th className="px-5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400">No customers yet — bookings appear here automatically.</td></tr>
              )}
              {data.items.map((c) => (
                <tr key={c.phone} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.phone}`}>
                  <td className="px-5 py-3 font-semibold">
                    <Link to={`/admin/customers/${c.phone}`} className="text-[#1A3C6E] hover:underline">{c.name}</Link>
                  </td>
                  <td className="px-5 py-3">{c.phone}</td>
                  <td className="px-5 py-3 capitalize">{c.city}</td>
                  <td className="px-5 py-3 capitalize">{c.segment}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.categories || []).map((id) => {
                        const cat = CATEGORIES.find((cc) => cc.id === id);
                        return <span key={id} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: cat?.bg || "#E2E8F0", color: cat?.color || "#475569" }}>{cat?.short || id}</span>;
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-semibold">{c.total_bookings}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{c.last_booking ? new Date(c.last_booking).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/admin/customers/${c.phone}`} className="p-1.5 rounded-md hover:bg-slate-200" title="View" data-testid={`customer-view-${c.phone}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Link>
                      <a href={`tel:${c.phone}`} className="p-1.5 rounded-md hover:bg-slate-200"><Phone className="w-4 h-4 text-slate-600" /></a>
                      <a href={`https://wa.me/91${c.phone}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-green-100"><MessageCircle className="w-4 h-4 text-[#22C55E]" /></a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3">
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1 rounded-md border">Previous</button>
          <button disabled={page * pageSize >= data.total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded-md border">Next</button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Per page</label>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="px-2 py-1 border rounded">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </AdminLayout>
  );
}
